import { useSelector } from "react-redux";
import { anatomyConstants } from "../../../constants/anatomyConstants";

const SurgeryPositionModal = ({
  goToSurgeryPosition,
  saveSurgeryPosition,
  addSurgeryPos,
  deleteSurgeryPos,
  setSelectedSurgeryPosId,
  selectedCase,
  selectedSurgeryPosId,
  closeSurgeryPositionMode,
  isMobile,
}) => {
  const selectedCaseDetail = useSelector(
    (state) => state.cases.selectedCaseDetail
  );
  const surgeryPositions = selectedCaseDetail?.surgeryPos || {};

  // Check if mobile portrait (upside down) view
  const isMobilePortrait =
    isMobile &&
    typeof window !== "undefined" &&
    window.innerWidth < window.innerHeight;

  return (
    <div
      className={`absolute ${
        isMobilePortrait ? "bottom-20 left-2" : "bottom-1 left-28"
      } w-auto flex flex-col items-center z-50`}
    >
      <div
        className="relative flex flex-col items-center space-y-2 px-3 pt-7 pb-3 bg-white rounded-lg shadow-lg border border-gray-200 w-48"
        style={{
          "--main-color": anatomyConstants.mainColor,
          "--main-color-bold": anatomyConstants.mainColorBold,
        }}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            closeSurgeryPositionMode();
          }}
          aria-label="Close"
          className="absolute top-2 right-2 w-5 h-5 rounded-full bg-gray-100 border border-[#0000001A] shadow-sm flex items-center justify-center text-gray-700 hover:bg-gray-200 z-10"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            className="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {Object.entries(surgeryPositions).map(([id, pos], index) => {
          const selected = selectedSurgeryPosId === id;
          const positionColors = [
            "bg-blue-50 border-blue-200 hover:bg-blue-100",
            "bg-green-50 border-green-200 hover:bg-green-100",
            "bg-purple-50 border-purple-200 hover:bg-purple-100",
            "bg-orange-50 border-orange-200 hover:bg-orange-100",
            "bg-pink-50 border-pink-200 hover:bg-pink-100",
          ];
          const selectedColors = [
            "bg-blue-100 border-blue-300",
            "bg-green-100 border-green-300",
            "bg-purple-100 border-purple-300",
            "bg-orange-100 border-orange-300",
            "bg-pink-100 border-pink-300",
          ];
          const colorClass = selected
            ? selectedColors[index]
            : positionColors[index];

          const leftDivider = selected
            ? `border-r ${
                index === 0
                  ? "border-blue-300"
                  : index === 1
                  ? "border-green-300"
                  : index === 2
                  ? "border-purple-300"
                  : index === 3
                  ? "border-orange-300"
                  : "border-pink-300"
              }`
            : `border-r ${
                index === 0
                  ? "border-blue-200"
                  : index === 1
                  ? "border-green-200"
                  : index === 2
                  ? "border-purple-200"
                  : index === 3
                  ? "border-orange-200"
                  : "border-pink-200"
              }`;

          const rightDivider = selected
            ? `border-l ${
                index === 0
                  ? "border-blue-300"
                  : index === 1
                  ? "border-green-300"
                  : index === 2
                  ? "border-purple-300"
                  : index === 3
                  ? "border-orange-300"
                  : "border-pink-300"
              }`
            : `border-l ${
                index === 0
                  ? "border-blue-200"
                  : index === 1
                  ? "border-green-200"
                  : index === 2
                  ? "border-purple-200"
                  : index === 3
                  ? "border-orange-200"
                  : "border-pink-200"
              }`;

          return (
            <div key={id} className="w-full">
              <div
                className={`inline-flex w-full items-stretch rounded-md overflow-hidden border transition ${colorClass}`}
              >
                {/* LEFT: Delete */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteSurgeryPos(id);
                  }}
                  className={`w-8 shrink-0 px-2 py-2 flex items-center justify-center text-[#757575] transition focus:outline-none ${leftDivider}`}
                  title="Delete Surgery Position"
                  aria-label="Delete Surgery Position"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5-4h4m-4 0a1 1 0 00-1 1v1h6V4a1 1 0 00-1-1m-4 0h4"
                    />
                  </svg>
                </button>

                {/* MIDDLE: Select only (two-line clamp with ellipsis) */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedSurgeryPosId(id);
                  }}
                  className="flex-1 min-w-0 px-3 py-2 text-sm font-inter font-medium text-[#757575] text-left focus:outline-none"
                  aria-pressed={selected}
                >
                  <span
                    className="block break-words"
                    style={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {pos.name || "Unnamed Surgery Pos"}
                  </span>
                </button>

                {/* RIGHT: Go (navigate) */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedSurgeryPosId(id);
                    goToSurgeryPosition(id);
                  }}
                  title="Go to Surgery Position"
                  aria-label="Go to Surgery Position"
                  className={`w-8 shrink-0 px-2 py-2 flex items-center justify-center text-[#757575] transition focus:outline-none ${rightDivider}`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </div>
          );
        })}

        {Object.keys(surgeryPositions).length > 0 && (
          <button
            title="Saves the current configuration as a surgery position."
            onClick={(e) => {
              e.stopPropagation();
              saveSurgeryPosition(selectedSurgeryPosId);
            }}
            className="w-full py-2 px-3 text-sm md:text-xs font-medium text-white rounded bg-[var(--main-color)] hover:bg-[var(--main-color-bold)]"
          >
            Save Surgery Position
          </button>
        )}

        {Object.keys(surgeryPositions).length < 5 && (
          <button
            title="Create new surgery position."
            onClick={(e) => {
              e.stopPropagation();
              addSurgeryPos();
            }}
            className="flex-1 py-2 px-3 text-sm md:text-xs font-medium text-white rounded bg-[var(--main-color)] hover:bg-[var(--main-color-bold)]"
          >
            Create New Surgery Position
          </button>
        )}
      </div>
    </div>
  );
};

export default SurgeryPositionModal;
